/**
 * PortalShell.tsx  -  Sidebar frame shared by the school admin, parent and
 * platform admin portals, so every adult screen looks like one product.
 */

import { ConfirmEmailBanner } from "@/components/ConfirmEmailBanner";
import { useState, type ReactNode } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { LogOut, Menu, X } from "lucide-react";
import { useAuthStore } from "@/store/authStore";
import { useLogout } from "@/hooks/useAuth";
import LitlingsMark from "@/components/LitlingsMark";
import { SkipLink } from "@/components/SkipLink";

export interface PortalNavItem {
  to: string;
  label: string;
  icon: typeof LogOut;
  end?: boolean;
  count?: number;
}

export function PortalShell({
  nav,
  portalLabel,
  banner,
}: {
  nav: PortalNavItem[];
  portalLabel: string;
  banner?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const user = useAuthStore((s) => s.user);
  const logout = useLogout();

  return (
    <div className="flex h-screen overflow-hidden bg-portal-bg">
      <SkipLink />
      {open && (
        <div
          className="fixed inset-0 z-20 bg-black/40 lg:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-30 flex w-60 flex-col border-r border-portal-border bg-portal-bg-alt transition-transform duration-300 lg:static lg:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex h-20 items-center gap-3 px-6">
          <LitlingsMark size={32} />
          <div className="min-w-0">
            <p className="font-display text-xl font-extrabold leading-none tracking-tight">
              Litlings
            </p>
            <p className="mt-1 truncate text-xs font-semibold text-portal-muted">
              {portalLabel}
            </p>
          </div>
        </div>

        <nav
          aria-label="Main navigation"
          className="flex-1 space-y-1 overflow-y-auto px-4"
        >
          {nav.map(({ to, icon: Icon, label, end, count }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-3 py-2.5 text-[15px] font-semibold transition-colors ${
                  isActive
                    ? "bg-brand-ink text-white"
                    : "text-portal-text-soft hover:bg-white hover:text-portal-text"
                }`
              }
            >
              <Icon className="h-[18px] w-[18px] flex-shrink-0" />
              <span className="flex-1">{label}</span>
              {!!count && count > 0 && (
                <span className="rounded-full bg-brand-red px-2 py-0.5 text-[11px] font-bold tabular-nums text-white">
                  {count > 99 ? "99+" : count}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-4">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-brand-yellow text-sm font-bold text-brand-ink">
              {user?.firstName?.[0]}
              {user?.lastName?.[0]}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="truncate text-xs text-portal-muted">
                {user?.school?.name ?? portalLabel}
              </p>
            </div>
          </div>
          <button
            onClick={() => logout.mutate()}
            className="mt-1 flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm font-semibold text-portal-muted transition-colors hover:bg-white hover:text-danger"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="flex h-14 flex-shrink-0 items-center gap-3 border-b border-portal-border bg-white px-4 lg:hidden">
          <button
            className="rounded-full p-2 hover:bg-portal-bg-alt"
            onClick={() => setOpen(!open)}
            aria-label={open ? "Close menu" : "Open menu"}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <p className="font-display text-lg font-extrabold">Litlings</p>
        </header>
        <ConfirmEmailBanner />
        {banner}
        <main
          id="main-content"
          className="portal-main flex-1 overflow-y-auto px-5 py-6 sm:px-8 sm:py-8"
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}
